//@ts-nocheck
import React, { useState } from 'react';
import { useRecordContext, useGetList } from 'react-admin';
import {
	Box,
	Card,
	CardContent,
	CircularProgress,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
	Chip,
	TablePagination,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { differenceInSeconds, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
	CONNECTOR_PROCESS_STOP_WITH_ERROR,
	connectorAccountProcessStatus,
	resolutionStatusLst,
} from '../../Services/connectorAccountProcess';

// #region Helpers
const getStatusName = (status: number, resolutionStatus: number) => {
	let statusObject = connectorAccountProcessStatus.find((item) => item.id === status);

	if (status === CONNECTOR_PROCESS_STOP_WITH_ERROR) {
		statusObject = resolutionStatusLst.find((item) => item.id === resolutionStatus);
	}

	return statusObject ? statusObject.name : 'Unknown status';
};

const getStatusColor = (status: number, resolutionStatus: number): string => {
	let statusObject = connectorAccountProcessStatus.find((item) => item.id === status);

	if (status === CONNECTOR_PROCESS_STOP_WITH_ERROR) {
		statusObject = resolutionStatusLst.find((item) => item.id === resolutionStatus);
	}

	return statusObject?.color ?? 'black';
};

const formatDuration = (seconds: number): string => {
	const h = Math.floor(seconds / 3600)
		.toString()
		.padStart(2, '0');
	const m = Math.floor((seconds % 3600) / 60)
		.toString()
		.padStart(2, '0');
	const s = Math.floor(seconds % 60)
		.toString()
		.padStart(2, '0');
	return `${h}:${m}:${s}`;
};
// #endregion

// #region ConnectorAccountLogs
export const ConnectorAccountLogs = () => {
	const record = useRecordContext();
	const [page, setPage] = useState(0);
	const [perPage, setPerPage] = useState(10);

	const { data, total, isLoading, error } = useGetList('logs', {
		pagination: { page: page + 1, perPage },
		sort: { field: 'startedAt', order: 'DESC' },
		filter: {
			projectFolderId: record?.projectFolderId,
			sku: record?.sku,
			accountId: record?.accountId,
		},
	});

	if (!record) {
		return (
			<Card>
				<CardContent>
					<Typography>Chargement...</Typography>
				</CardContent>
			</Card>
		);
	}

	if (isLoading) {
		return (
			<Card>
				<CardContent>
					<Box display="flex" justifyContent="center" p={3}>
						<CircularProgress />
					</Box>
				</CardContent>
			</Card>
		);
	}

	if (error) {
		return (
			<Card>
				<CardContent>
					<Typography color="error">Erreur lors du chargement des logs</Typography>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Historique des exécutions
				</Typography>
				{data && data.length > 0 ? (
					<>
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Type</TableCell>
										<TableCell>Status</TableCell>
										<TableCell>Démarré</TableCell>
										<TableCell>Durée</TableCell>
										<TableCell>Erreur</TableCell>
										<TableCell></TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{data.map((log: any) => {
										const now = new Date();
										const updatedAt = log.updatedAt ? new Date(log.updatedAt) : undefined;
										const tooOld =
											updatedAt &&
											(now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60) > 24 &&
											(log.status === 0 || log.status === 50);

										let startedAtStr = '—';
										let durationStr = '—';

										try {
											const startedAt = new Date(log.startedAt);
											startedAtStr = format(startedAt, "dd MMM yyyy 'à' HH:mm", {
												locale: fr,
											});

											const endedAt = log.endedAt ? new Date(log.endedAt) : new Date();
											const seconds = differenceInSeconds(endedAt, startedAt);
											durationStr = formatDuration(seconds);
										} catch (e) {
											// ignore
										}

										return (
											<TableRow key={log.id} hover>
												<TableCell>
													<Chip
														label={log.processType}
														size="small"
														variant="outlined"
														color={
															log.processType === 'force'
																? 'primary'
																: log.processType === 'history'
																? 'secondary'
																: 'default'
														}
													/>
												</TableCell>
												<TableCell>
													<Box sx={{ display: 'flex', alignItems: 'center' }}>
														<Typography
															variant="body2"
															sx={{
																color: getStatusColor(log.status, log.resolutionStatus),
																fontWeight: 'bold',
															}}
														>
															{getStatusName(log.status, log.resolutionStatus)}
														</Typography>
														{tooOld && (
															<WarningAmberOutlinedIcon
																sx={{ color: '#ff9800', ml: 0.5 }}
																titleAccess="Non actualisé depuis plus de 24h"
																fontSize="small"
															/>
														)}
													</Box>
												</TableCell>
												<TableCell>
													<Typography variant="body2">{startedAtStr}</Typography>
												</TableCell>
												<TableCell>
													<Typography variant="body2">{durationStr}</Typography>
												</TableCell>
												<TableCell>
													<Typography
														variant="body2"
														sx={{
															maxWidth: 200,
															overflow: 'hidden',
															textOverflow: 'ellipsis',
															whiteSpace: 'nowrap',
														}}
														title={log.resolutionMessage || log.errorDetails}
													>
														{log.resolutionMessage || log.errorDetails || '—'}
													</Typography>
												</TableCell>
												<TableCell>
													<Box sx={{ display: 'flex', alignItems: 'center' }}>
														<IconButton
															size="small"
															onClick={() => window.open(`${window.location.origin}/logs/${log.id}/show`, '_blank')}
															title="Voir les détails"
														>
															<OpenInNewIcon fontSize="small" />
														</IconButton>
													</Box>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							component="div"
							count={total || 0}
							page={page}
							onPageChange={(_, newPage) => setPage(newPage)}
							rowsPerPage={perPage}
							onRowsPerPageChange={(e) => {
								setPerPage(parseInt(e.target.value, 10));
								setPage(0);
							}}
							rowsPerPageOptions={[5, 10, 25, 50]}
							labelRowsPerPage="Par page"
						/>
					</>
				) : (
					<Typography variant="body2" color="text.secondary">
						Aucun log trouvé pour ce connecteur
					</Typography>
				)}
			</CardContent>
		</Card>
	);
};
// #endregion
